import { Editor, TLShape } from "tldraw";

import {
  isCustomNodeRootShape,
  isFeedShape,
  isLogicShape,
  LayoutBinding,
} from "@/components/canvas-editor/shapes/shapes";

/**
 * How many OTHER conditions share the `and` this node sits in.
 *
 * Only the nearest enclosing group counts, and only when it is an `and`. An
 * `or` is not a narrowing: every post reaching it is offered to every branch,
 * so a node inside one is a second way to match rather than a restriction on
 * the feed. `unshieldedMeteredNodes` in `src/lib/rate-limited-nodes.ts` draws
 * the same line for the same reason, as does `app/quota_shielding.py`.
 *
 * Returns 0 for a node whose parent is the feed itself: a filter that IS the
 * whole feed restricts nothing beyond itself, and saying otherwise would put a
 * warning on the 724 live feeds that are exactly one Users node.
 *
 * Placeholder bindings are the drop-target ghost the layout binding inserts
 * while a shape is being dragged, not a sibling condition, so they are not
 * counted -- otherwise the warning would flicker on during a drag.
 */
export function andSiblingCount(editor: Editor, shape: TLShape): number {
  const parentBinding = editor
    .getBindingsToShape<LayoutBinding>(shape, "layout")
    .find((binding) => !binding.props.placeholder);

  if (!parentBinding) return 0;

  const parent = editor.getShape(parentBinding.fromId);

  if (!parent || !isLogicShape(parent) || parent.props.type !== "and") {
    return 0;
  }

  const conjuncts = editor
    .getBindingsFromShape<LayoutBinding>(parent, "layout")
    .filter((binding) => !binding.props.placeholder);

  return Math.max(0, conjuncts.length - 1);
}

/**
 * Whether this node is a ROOT CONJUNCT: the feed's whole rule, or a direct child of
 * the `and` that is.
 *
 * That is the only place the serve-time label stage looks (feed-processor builds
 * `label_exclusions` from the live manifest's root conjuncts, and feeder-rs applies
 * nothing else). A post label anywhere deeper is inert, and an account-label exclude
 * deeper down still works at match time but loses the serve-time half -- so the node
 * says so.
 *
 * The custom-node editor's root counts as a feed root here: it is the only root that
 * canvas has. Whether the component ends up at the top of a feed is decided where it
 * is installed, and is not knowable from inside its definition.
 *
 * Returns false for a node not bound into the feed at all; the caller decides whether
 * an unbound node is worth a note (it is not: it is not filtering anything yet).
 */
export function isRootConjunct(editor: Editor, shape: TLShape): boolean {
  const parentOf = (child: TLShape) => {
    const binding = editor
      .getBindingsToShape<LayoutBinding>(child, "layout")
      .find((b) => !b.props.placeholder);
    return binding ? editor.getShape(binding.fromId) : undefined;
  };
  const isRoot = (candidate: TLShape | undefined) =>
    Boolean(
      candidate && (isFeedShape(candidate) || isCustomNodeRootShape(candidate)),
    );

  const parent = parentOf(shape);
  if (!parent) return false;
  if (isRoot(parent)) return true;

  return (
    isLogicShape(parent) &&
    parent.props.type === "and" &&
    isRoot(parentOf(parent))
  );
}
